import { CapsuleState, UserState } from 'lib/types/models'
import React, { useEffect, useState } from 'react'
import { dateAndTimeDiff } from 'lib/utils/dateAndTimeDiff'
import { useSelector } from 'react-redux'
import { AppState } from 'store'
import { Link, useParams } from 'react-router-dom'
import { UserGuide } from 'components/UserGuide'
import CapsuleImage from '../../assets/kilit.png'
import GooglePlayLogo from '../../assets/google-play.png'
import Logo from '../../assets/logo.png'

type Props = {
  capsule: CapsuleState

  onTimeIsOver: () => void
}

const CapsuleLocked: React.FC<Props> = ({ capsule, onTimeIsOver }) => {
  const [diff, setDiff] = useState<any>(null)
  const [showGuide, setShowGuide] = useState(false)

  const user = useSelector<AppState, UserState>(({ user }) => user)

  const params = useParams()

  useEffect(() => {
    const openingDate = new Date(capsule.opening_date as string)

    const calculateDiff = () => {
      const now = new Date()
      if (openingDate < now) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 }
      }
      return dateAndTimeDiff(openingDate, now)
    }

    setDiff(calculateDiff())

    const interval = setInterval(() => {
      setDiff(calculateDiff())

      if (new Date(capsule.opening_date as string) < new Date()) {
        clearInterval(interval)
        onTimeIsOver()
      }
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [])

  if (!diff) {
    return null
  }

  return (
    <>
      {showGuide && <UserGuide onClose={() => setShowGuide(false)} />}

      <div className="flex flex-col h-full bg-[#000]">
        <div className="pt-[32px] overflow-hidden flex flex-col flex-1 relative">
          <div className="absolute top-[20px] left-[50%] transform translate-x-[-50%]">
            <img src={Logo} alt="logo" className="h-[25px]" />
          </div>
          <div className="flex flex-col pt-[16px] pb-[32px] h-full justify-center">
            <div className="mb-[50px]">
              <img
                src={CapsuleImage}
                alt="capsule"
                className="w-full h-[225px] object-contain"
              />
            </div>
            <div className="mb-[32px] flex flex-col items-center">
              <div className="text-h4 font-semibold text-white text-center italic">
                {capsule.name || 'Kapsülüm'}
              </div>
            </div>
            <div className="flex justify-center px-[8px] gap-[20px]">
              <div className="flex flex-col items-center">
                <div className="text-[40px] leading-[35px] text-white mb-[8px]">
                  {diff.days}
                </div>
                <div className="text-sm text-white">GÜN</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-[40px] leading-[35px] text-white mb-[8px]">
                  {diff.hours}
                </div>
                <div className="text-sm text-white">SAAT</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-[40px] leading-[35px] text-white mb-[8px]">
                  {diff.minutes}
                </div>
                <div className="text-sm text-white">DAKİKA</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-[40px] leading-[35px] text-white mb-[8px]">
                  {diff.seconds}
                </div>
                <div className="text-sm text-white">SANİYE</div>
              </div>
            </div>
          </div>
          {capsule.receiver_id === null && (
            <div className="p-[24px] pb-[32px] gap-[16px] flex flex-col relative z-10">
              <Link
                to={`/register?capsule=${params.uuid}`}
                className="p-[16px] flex justify-center rounded-[100px] bg-dark-100 text-lg font-semibold text-white relative overflow-hidden"
              >
                Alıcı Hesabı Oluştur
              </Link>
            </div>
          )}
          <div
            className="px-[24px] pb-[24px] gap-[16px] flex flex-col relative z-10"
            onClick={() => setShowGuide(true)}
          >
            <div className="p-[16px] flex justify-center rounded-[100px] bg-dark-100 text-lg font-semibold text-white relative overflow-hidden">
              Kullanım Kılavuzu
            </div>
          </div>
          {capsule.receiver_id === user?.id && (
            <div className="p-[24px] pb-[32px] gap-[16px] flex flex-col relative z-10">
              <a
                href="https://play.google.com/store/apps/details?id=com.texttonext.app"
                className="py-[12px] px-[16px] flex justify-center rounded-[100px] text-lg font-semibold text-white border-2 border-dark-100"
              >
                <img src={GooglePlayLogo} alt="google-play" />
              </a>

              <a
                href="https://apps.apple.com/am/app/texttonext/id6473700980"
                className="py-[14px] px-[16px] flex justify-center rounded-[100px] text-lg font-semibold text-white"
              >
                <svg
                  width="120"
                  height="30"
                  viewBox="0 0 120 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M17.7744 0C17.9046 1.74169 17.367 3.46678 16.2758 4.80892C15.2307 6.14065 13.5913 6.93487 11.9236 6.91727C11.8172 5.24215 12.38 3.54742 13.4614 2.28639C14.5673 0.995751 16.1073 0.179401 17.7744 0ZM23.1175 10.2337C21.1918 11.4467 19.9779 13.638 19.9488 15.9539C19.9516 18.5703 21.5222 20.9953 23.8741 22.0145C23.4169 23.536 22.7279 24.9739 21.8324 26.275C20.6298 28.1181 19.3689 29.9189 17.3676 29.9521C16.4158 29.9747 15.7733 29.6942 15.1038 29.402C14.4055 29.0972 13.6778 28.7796 12.5391 28.7796C11.3314 28.7796 10.5711 29.1074 9.83786 29.4236C9.2042 29.6969 8.59072 29.9614 7.72614 29.9981C5.82022 30.0705 4.36362 28.0309 3.1173 26.205C0.626248 22.4762 -1.31348 15.6966 1.28701 11.0836C2.49676 8.85626 4.81483 7.4078 7.30136 7.32549C8.38232 7.3027 9.41958 7.72958 10.329 8.10383C11.0245 8.39005 11.6451 8.64549 12.1534 8.64549C12.6002 8.64549 13.2036 8.40014 13.9067 8.1142C15.0144 7.66377 16.3698 7.11263 17.7509 7.26117C19.8679 7.32903 21.9027 8.4561 23.1175 10.2337ZM72.4775 18.3128C72.6421 21.0318 74.8536 22.7706 78.145 22.7706C81.6627 22.7706 83.8639 20.9474 83.8639 18.0388C83.8639 15.7519 82.609 14.4873 79.5645 13.7601L77.929 13.3491C75.9953 12.8854 75.2136 12.2636 75.2136 11.1781C75.2136 9.80812 76.4273 8.91233 78.2479 8.91233C79.9759 8.91233 81.169 9.78704 81.385 11.1887H83.6273C83.4936 8.62779 81.2925 6.82568 78.2787 6.82568C75.0387 6.82568 72.8787 8.62779 72.8787 11.3362C72.8787 13.5704 74.1027 14.8983 76.7873 15.5411L78.7004 16.0154C80.665 16.4896 81.529 17.1852 81.529 18.3444C81.529 19.6934 80.1713 20.6734 78.3199 20.6734C76.3347 20.6734 74.9564 19.7566 74.761 18.3128H72.4775ZM86.4696 11.3799V8.57357H88.6032V11.3799H90.9452V13.2203H88.6032V18.9142C88.6032 20.0734 89.0696 20.5817 90.1315 20.5817C90.4495 20.5817 90.6365 20.5626 90.9198 20.5335L90.9452 20.5309V22.3509C90.5979 22.4119 90.2009 22.4628 89.7841 22.4628C87.4123 22.4628 86.4696 21.6087 86.4696 19.4735V13.2203H84.7527V11.3799H86.4696ZM102.078 16.8603C102.078 20.4496 100.143 22.6153 96.9873 22.6153C93.8215 22.6153 91.8864 20.4496 91.8864 16.8603C91.8864 13.2914 93.8315 11.1155 96.9873 11.1155C100.133 11.1155 102.078 13.2914 102.078 16.8603ZM94.0895 16.8603C94.0895 19.2803 95.1315 20.6733 96.9873 20.6733C98.8331 20.6733 99.885 19.2701 99.885 16.8603C99.885 14.4607 98.8331 13.0576 96.9873 13.0576C95.1315 13.0576 94.0895 14.4607 94.0895 16.8603ZM103.734 22.4018H105.868V15.8639C105.868 14.3082 107.009 13.2609 108.626 13.2609C109.003 13.2609 109.639 13.3321 109.817 13.3931V11.2375C109.589 11.1765 109.182 11.146 108.865 11.146C107.455 11.146 106.255 11.9391 105.947 13.0271H105.788V11.329H103.734V22.4018ZM117.775 15.8435C117.725 14.1049 116.693 12.9762 115.115 12.9762C113.537 12.9762 112.436 14.115 112.316 15.8435H117.775ZM115.194 20.7546C116.484 20.7546 117.368 20.2767 117.765 19.4124H119.819C119.333 21.3952 117.606 22.6153 115.135 22.6153C112.039 22.6153 110.143 20.4394 110.143 16.901C110.143 13.3626 112.078 11.1155 115.125 11.1155C118.132 11.1155 119.948 13.2203 119.948 16.6976V17.4602H112.316V17.5822C112.386 19.5243 113.487 20.7546 115.194 20.7546ZM46.9814 13.1592C47.6363 11.8984 48.9066 11.146 50.3753 11.146C53.1242 11.146 54.8609 13.3423 54.8609 16.8603C54.8609 20.3784 53.1242 22.5848 50.3952 22.5848C48.857 22.5848 47.6363 21.8934 47.0707 20.7241H46.902V26.0825H44.7684V11.329H46.8226V13.1592H46.9814ZM46.8623 16.8705C46.8623 19.1786 47.9837 20.6224 49.77 20.6224C51.586 20.6224 52.6677 19.2091 52.6677 16.8603C52.6677 14.5217 51.586 13.1084 49.7799 13.1084C47.9837 13.1084 46.8623 14.5522 46.8623 16.8705ZM62.3268 11.146C60.8581 11.146 59.5878 11.8984 58.9329 13.1592H58.7741V11.329H56.7198V26.0825H58.8535V20.7241H59.0222C59.5878 21.8934 60.8085 22.5848 62.3467 22.5848C65.0757 22.5848 66.8124 20.3784 66.8124 16.8603C66.8124 13.3423 65.0757 11.146 62.3268 11.146ZM61.7215 20.6224C59.9352 20.6224 58.8138 19.1786 58.8138 16.8705C58.8138 14.5522 59.9352 13.1084 61.7314 13.1084C63.5375 13.1084 64.6192 14.5217 64.6192 16.8603C64.6192 19.2091 63.5375 20.6224 61.7215 20.6224ZM43.3883 22.4018H40.9403L39.5825 18.3655H33.9254L32.5573 22.4018H30.1916L35.5505 7.19454H38.0294L43.3883 22.4018ZM36.8465 9.95566H36.6717L34.5116 16.3842H38.9962L36.8465 9.95566Z"
                    fill="white"
                  />
                </svg>
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default CapsuleLocked
